import { useState, useEffect } from 'preact/hooks';
import { SlidersHorizontal, Thermometer, Info, Palette, Lightning, Bell, PaperPlaneRight } from '@phosphor-icons/react';
import { api } from '../../services/api.js';
import { useToast } from '../shared/Toast.js';

interface Props {
  coreTempThreshold: number;
  memoryTempThreshold: number;
  onCoreTempChange: (v: number) => void;
  onMemoryTempChange: (v: number) => void;
  electricityRate: number;
  onElectricityRateChange: (v: number) => void;
  theme: string;
  onThemeChange: (t: string) => void;
}

export function SettingsPage({ coreTempThreshold, memoryTempThreshold, onCoreTempChange, onMemoryTempChange, electricityRate, onElectricityRateChange, theme, onThemeChange }: Props) {
  const { show: toast } = useToast();

  const [telegramToken, setTelegramToken] = useState('');
  const [telegramChat, setTelegramChat] = useState('');
  const [telegramConfigured, setTelegramConfigured] = useState(false);
  const [chatConfigured, setChatConfigured] = useState(false);

  useEffect(() => {
    api.getAlertSettings().then((s) => {
      setTelegramConfigured(!!s.telegramBotToken && s.telegramBotToken !== '');
      setChatConfigured(!!s.telegramChatId && s.telegramChatId !== '');
    }).catch(() => {});
  }, []);

  const handleTelegramSave = async () => {
    if (!telegramToken && !telegramConfigured) {
      toast('Заполните токен бота', 'warning');
      return;
    }
    if (!telegramChat && !chatConfigured) {
      toast('Заполните Chat ID', 'warning');
      return;
    }
    try {
      await api.saveAlertSettings({
        telegramBotToken: telegramToken || '__keep__',
        telegramChatId: telegramChat || '__keep__',
        enableOfflineAlert: true,
        enableTempAlert: true,
        enableHashrateDropAlert: true,
        hashrateDropThreshold: 20,
      });
      setTelegramToken('');
      setTelegramConfigured(true);
      toast('Telegram настройки сохранены на сервере', 'success');
    } catch {
      toast('Ошибка сохранения', 'error');
    }
  };

  const handleTestTelegram = async () => {
    try {
      await api.testAlert();
      toast('Тестовое сообщение отправлено', 'success');
    } catch {
      toast('Ошибка отправки. Сохраните настройки сначала', 'error');
    }
  };

  return (
    <>

      <div class="settings-grid">
        {/* General */}
        <div class="data-card">
          <div class="data-card-header">
            <SlidersHorizontal size={16} />
            <span>Общие настройки</span>
          </div>
          <div class="data-card-body">
            <div class="setting-item">
              <div>
                <div class="setting-label">Интервал обновления</div>
                <div class="setting-desc">Частота обновления данных с ферм</div>
              </div>
              <select class="setting-select" value="10">
                <option value="5">5 секунд</option>
                <option value="10">10 секунд</option>
                <option value="30">30 секунд</option>
              </select>
            </div>
            <div class="setting-item">
              <div>
                <div class="setting-label">Уведомления</div>
                <div class="setting-desc">Push-уведомления об изменениях</div>
              </div>
              <label class="toggle">
                <input type="checkbox" checked />
                <span class="toggle-track" />
                <span class="toggle-thumb" />
              </label>
            </div>
          </div>
        </div>

        {/* Theme */}
        <div class="data-card">
          <div class="data-card-header">
            <Palette size={16} />
            <span>Тема оформления</span>
          </div>
          <div class="data-card-body">
            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              {(['dark', 'classic', 'oled'] as const).map((t) => (
                <button
                  key={t}
                  class={`btn ${theme === t ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => onThemeChange(t)}
                  style={{ minWidth: '80px', justifyContent: 'center' }}
                >
                  {t === 'dark' ? 'Dark' : t === 'classic' ? 'Classic' : 'OLED'}
                </button>
              ))}
            </div>
            <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--tx-muted)' }}>
              {theme === 'oled' ? 'Чёрный фон для AMOLED экранов — экономит батарею' :
               theme === 'classic' ? 'Классическая тёмно-синяя тема' : 'Стандартная тёмная тема'}
            </div>
          </div>
        </div>

        {/* Temperature */}
        <div class="data-card">
          <div class="data-card-header">
            <Thermometer size={16} />
            <span>Температурные алерты</span>
          </div>
          <div class="data-card-body">
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--tx-secondary)', marginBottom: 'var(--space-4)' }}>
              Критические пороги температуры GPU — при превышении ячейка подсветится алертом
            </p>
            <div class="setting-item">
              <div>
                <div class="setting-label">Ядро GPU (&deg;C)</div>
                <div class="setting-desc">Критический порог — ядро</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <input
                  type="number"
                  class="setting-input"
                  value={coreTempThreshold}
                  min={70}
                  max={100}
                  onChange={(e) => onCoreTempChange(parseInt((e.target as HTMLInputElement).value, 10))}
                />
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--tx-muted)' }}>&deg;C</span>
              </div>
            </div>
            <div class="setting-item">
              <div>
                <div class="setting-label">Память VRAM (&deg;C)</div>
                <div class="setting-desc">Критический порог — VRAM</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <input
                  type="number"
                  class="setting-input"
                  value={memoryTempThreshold}
                  min={80}
                  max={110}
                  onChange={(e) => onMemoryTempChange(parseInt((e.target as HTMLInputElement).value, 10))}
                />
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--tx-muted)' }}>&deg;C</span>
              </div>
            </div>
          </div>
        </div>

        {/* Electricity */}
        <div class="data-card">
          <div class="data-card-header">
            <Lightning size={16} />
            <span>Электричество</span>
          </div>
          <div class="data-card-body">
            <div class="setting-item">
              <div>
                <div class="setting-label">Тариф электроэнергии</div>
                <div class="setting-desc">Используется для расчёта прибыли</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <input
                  type="number"
                  class="setting-input"
                  value={electricityRate}
                  min={0}
                  max={50}
                  step={0.1}
                  onChange={(e) => onElectricityRateChange(parseFloat((e.target as HTMLInputElement).value) || 0)}
                />
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--tx-muted)' }}>₽/кВтч</span>
              </div>
            </div>
          </div>
        </div>

        {/* Telegram */}
        <div class="data-card">
          <div class="data-card-header">
            <Bell size={16} />
            <span>Telegram алерты</span>
          </div>
          <div class="data-card-body">
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--tx-secondary)', marginBottom: 'var(--space-4)' }}>
              Получайте уведомления об offline, перегреве и падении хэшрейта
            </p>
            <div class="setting-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--space-2)' }}>
              <div class="setting-label">Bot Token</div>
              <input
                type="password"
                class="setting-input"
                style={{ width: '100%', textAlign: 'left' }}
                placeholder={telegramConfigured ? '••••••••• (сохранён)' : '123456:ABC-DEF...'}
                value={telegramToken}
                onInput={(e) => setTelegramToken((e.target as HTMLInputElement).value)}
              />
              {telegramConfigured && !telegramToken && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--lime)' }}>Токен сохранён на сервере. Оставьте пустым, чтобы не менять.</div>
              )}
            </div>
            <div class="setting-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--space-2)' }}>
              <div class="setting-label">Chat ID</div>
              <input
                type="password"
                class="setting-input"
                style={{ width: '100%', textAlign: 'left' }}
                placeholder={chatConfigured ? '••••••••• (сохранён)' : '-1001234567890'}
                value={telegramChat}
                onInput={(e) => setTelegramChat((e.target as HTMLInputElement).value)}
              />
              {chatConfigured && !telegramChat && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--lime)' }}>Chat ID сохранён на сервере. Оставьте пустым, чтобы не менять.</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
              <button class="btn btn-primary" onClick={handleTelegramSave}>Сохранить</button>
              <button class="btn btn-secondary" onClick={handleTestTelegram}>
                <PaperPlaneRight size={12} />
                <span>Тест</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 'var(--space-4)', fontSize: 'var(--text-xs)', color: 'var(--tx-muted)' }}>
        <Info size={12} />
        <span>Все настройки хранятся на сервере.</span>
      </div>
    </>
  );
}
